import React from 'react';
import { motion } from 'framer-motion';
import { Mic, Square } from 'lucide-react';

interface MicButtonProps {
  isRecording: boolean;
  isConnecting: boolean;
  /** 0..1 normalized audio level (only meaningful while recording). */
  level: number;
  onToggle: () => void;
  className?: string;
}

/**
 * Mic button with three states:
 *   - Idle:        subtle violet halo pulse — hints "tap to talk"
 *   - Connecting:  disabled, muted
 *   - Recording:   two concentric rings that scale with the audio level so
 *                  the user sees their voice is being picked up
 */
export const MicButton: React.FC<MicButtonProps> = ({
  isRecording,
  isConnecting,
  level,
  onToggle,
  className = '',
}) => {
  // Map level into ring scale/opacity for a responsive "speaking" pulse.
  // We clamp the ranges so even low-level speech moves the rings a bit.
  const ringScaleA = 1 + Math.min(level * 1.2, 0.9);
  const ringScaleB = 1 + Math.min(level * 1.8, 1.4);

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      {/* Idle halo: very soft pulse to invite interaction. */}
      {!isRecording && !isConnecting && (
        <motion.span
          aria-hidden
          className="absolute inset-0 rounded-full bg-purple/25"
          animate={{ scale: [1, 1.25, 1], opacity: [0.45, 0, 0.45] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      {/* Recording rings driven by audio level. */}
      {isRecording && (
        <>
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-full bg-purple/30"
            animate={{ scale: ringScaleA, opacity: 0.6 - level * 0.3 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18, mass: 0.4 }}
          />
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-full bg-purple/20"
            animate={{ scale: ringScaleB, opacity: 0.45 - level * 0.25 }}
            transition={{ type: 'spring', stiffness: 180, damping: 20, mass: 0.5 }}
          />
        </>
      )}

      <button
        type="button"
        onClick={onToggle}
        disabled={isConnecting}
        aria-label={isRecording ? 'Stop dictation' : 'Start dictation'}
        aria-pressed={isRecording}
        className={[
          'relative inline-flex items-center justify-center h-11 w-11 rounded-full',
          'transition-all focus:outline-none focus:ring-2 focus:ring-purple/60',
          isRecording
            ? 'bg-purple text-dark-invirtus shadow-[0_0_0_2px_#fff,0_0_18px_rgba(217,173,255,0.55)]'
            : isConnecting
            ? 'bg-gray-100 text-gray-400 cursor-wait'
            : 'bg-white border border-gray-300 text-gray-600 hover:border-purple hover:text-dark-invirtus shadow-sm hover:shadow-[0_0_0_4px_rgba(217,173,255,0.18)]',
        ].join(' ')}
      >
        {isRecording ? <Square className="h-4 w-4" fill="currentColor" /> : <Mic className="h-5 w-5" />}
      </button>
    </div>
  );
};
